// Normalización de errores HTTP del interceptor de axios.
//
// Histórico: el interceptor rechaza con `error.response.data` (el body
// crudo). Así los callers nunca veían el status (`err.response.status`
// era siempre undefined) y los checks `err?.response?.status === 409`
// eran código muerto.
//
// Compatibilidad total: el rechazo conserva EXACTAMENTE el mismo valor
// que antes (hay decenas de callers que hacen `enqueueSnackbar(err)`,
// `{error}` en JSX o guardan el error en Redux; cambiar `undefined` o un
// string por un objeto los rompería). Solo cuando el body es un objeto
// le añadimos metadatos NO enumerables — no cambian JSON.stringify,
// spreads, ni lo que se persiste en Redux:
//   status, response: { status, data }, isNetworkError, errorCode (si
//   el body traía `code` y no `errorCode`).
//
// Formas posibles del rechazo y cómo leerlas (usar siempre los helpers):
//   - body objeto          → objeto anotado (status disponible)
//   - body string / vacío  → el mismo string / "" (sin status)
//   - sin respuesta        → undefined (red caída, timeout, abort, CORS)

const defineHidden = (target, key, value) => {
  try {
    Object.defineProperty(target, key, {
      value,
      enumerable: false,
      configurable: true,
      writable: true,
    });
  } catch {
    /* objeto congelado → sin metadatos */
  }
};

/** Valor con el que el interceptor rechaza. Mismo valor que el histórico. */
export function normalizeHttpError(error) {
  const response = error?.response;
  if (!response) return undefined;
  const { status, data } = response;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    defineHidden(data, "status", status);
    defineHidden(data, "response", { status, data });
    defineHidden(data, "isNetworkError", false);
    if (data.errorCode == null && typeof data.code === "string") {
      defineHidden(data, "errorCode", data.code);
    }
  }
  return data;
}

export function errorStatus(err) {
  if (err == null || typeof err !== "object") return undefined;
  if (typeof err.status === "number") return err.status;
  if (typeof err.response?.status === "number") return err.response.status;
  return undefined;
}

export function errorCodeOf(err) {
  if (err == null) return undefined;
  if (typeof err === "string") {
    // Algunos endpoints devuelven el código como string plano
    // (p. ej. "MISSING_PRINTERS").
    return /^[A-Z][A-Z0-9_]{2,}$/.test(err) ? err : undefined;
  }
  if (typeof err !== "object") return undefined;
  const direct = err.errorCode ?? err.response?.data?.errorCode;
  if (direct) return direct;
  const code = err.code ?? err.response?.data?.code;
  return typeof code === "string" ? code : undefined;
}

/**
 * true si no hubo respuesta del servidor (o no se puede saber): el
 * rechazo histórico de red es `undefined`, y un body vacío tampoco
 * permite distinguir. Para cobros esto significa "resultado incierto".
 */
export function isUncertainOutcome(err) {
  if (err == null || err === "") return true;
  if (typeof err !== "object") return false;
  if (err.isNetworkError === true) return true;
  if (err.isAxiosError === true && !err.response) return true;
  const st = errorStatus(err);
  return st === 0 || st === 502 || st === 503 || st === 504;
}

export function errorMessage(err, fallback = "Ha ocurrido un error") {
  if (err == null || err === "") return fallback;
  if (typeof err === "string") return err;
  if (typeof err !== "object") return String(err);
  const body =
    err.response?.data && typeof err.response.data === "object" ? err.response.data : err;
  return (
    (typeof body.message === "string" && body.message) ||
    (typeof body.error === "string" && body.error) ||
    (typeof body.title === "string" && body.title) ||
    (typeof body.detail === "string" && body.detail) ||
    (typeof err.message === "string" && err.message) ||
    fallback
  );
}
